import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SimpleModalService } from '@looorent/ngx-simple-modal';
import { EnforcementService, AddCitationCommunicationRequest, CitationCommunicationChannel, CitationCommunicationDirection, CitationDetailResponse, WorkflowTransitionDto, InfringementResponse, NoticeTriggerGroupDto, CitationNoticeStatus } from 'src/app/Services/enforcement.service';
import { StructureViewerLayer } from 'src/app/shared/structureviewermaps/structureviewerlayers';
import { Busyable } from 'src/app/shared/editors/busyable';
import { ActionHistoryModalComponent } from './action-history-modal/action-history-modal.component';
import { StatusChangeModalComponent } from './status-change-modal/status-change-modal.component';
import { VoidInfringementModalComponent } from './void-infringement-modal/void-infringement-modal.component';
import { AddcitationmanualpaymentmodalComponent } from './manual-payment-modal/addcitationmanualpaymentmodal.component';
import { CitationpaymentrefundComponent } from './refund-payment-modal/citationpaymentrefund.component';
import { AddCitationAdjustmentComponent } from './add-adjustment-model/addadjustment.component';
import { AddNoteModalComponent } from 'src/app/shared/modals/add-note-modal/add-note-modal.component';
import { MediaService } from 'src/app/Services/media.service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';

type DocumentTemplateDto = { Id: string, Name: string, TargetType?: string | null };
type CitationNoticeListItem = {
  Id: number;
  CitationId: number;
  DocumentTemplateId: string;
  DocumentTemplateName?: string | null;
  MediaId: number | null;
  GeneratedAtUtc: string;
  GeneratedAtLocalFriendlyString?: string | null;
  Status?: CitationNoticeStatus;
  StatusName?: string | null;
};


@Component({
    selector: 'app-citation',
    templateUrl: './citation.component.html',
    styleUrls: ['./citation.component.scss'],
    standalone: false
})
export class CitationComponent extends Busyable implements OnInit, OnDestroy {
  public Model: CitationDetailResponse | null = null;
  public StructureViewerLayers: StructureViewerLayer[] = [];
  public StructureSearchTerm!: string;
  public StructureId!: string;
  public StructureViewerForType!: string;
  public communicationForm!: FormGroup;
  public communicationDirections = [
    { value: CitationCommunicationDirection.Outbound, label: 'Outbound' },
    { value: CitationCommunicationDirection.Inbound, label: 'Inbound' }
  ];
  public communicationChannels = [
    { value: CitationCommunicationChannel.Email, label: 'Email' },
    { value: CitationCommunicationChannel.SystemNote, label: 'System Note' },
    { value: CitationCommunicationChannel.Sms, label: 'SMS' },
    { value: CitationCommunicationChannel.Phone, label: 'Phone' },
    { value: CitationCommunicationChannel.PortalMessage, label: 'Portal Message' },
    { value: CitationCommunicationChannel.PostalMail, label: 'Postal Mail' }
  ];

  // Freeze-related properties
  public freezeType: '' | 'indefinite' | 'until' = '';
  public freezeUntilDate: string = '';
  public freezeReason: string = '';
  public clearFreezeReason: string = '';
  public freezeBusy: boolean = false;

  // Note-related properties
  public noteBusy: boolean = false;


  // Escalation schedule visibility
  public showEscalationSchedule: boolean = false;

  public noticeBusy: boolean = false;
  public noticeTriggerGroup: NoticeTriggerGroupDto | null = null;
  public selectedNoticeTriggerId: number | null = null;
  public templates: DocumentTemplateDto[] = [];
  public templatesLoaded: boolean = false;
  public citationNotices: CitationNoticeListItem[] = [];
  public noticesLoading: boolean = false;

  public get hasNotices(): boolean {
    return (this.citationNotices?.length ?? 0) > 0;
  }

  public get showCitationNoticesPanel(): boolean {
    return this.canGenerateNoticeLetters || this.hasNotices;
  }

  public getNoticeStatusName(n: CitationNoticeListItem): string {
    if (n.StatusName) return n.StatusName;
    if (n.Status === CitationNoticeStatus.Pending) return 'Pending';
    if (n.Status === CitationNoticeStatus.Collected) return 'Collected';
    if (n.Status === CitationNoticeStatus.Failed) return 'Failed';
    return '';
  }

  public get canGenerateNoticeLetters(): boolean {
    return true;
  }

  constructor(
    private route: ActivatedRoute,
    private modalService: SimpleModalService,
    public enforcementService: EnforcementService,
    private formBuilder: FormBuilder,
    private mediaService: MediaService,
    private toastr: ToastrService
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.initializeCommunicationForm();
    const id = this.route.snapshot.params['id'];
    if (id) {
      await this.loadCitation(id);
    }
    await Promise.all([
      this.loadTemplates(),
      this.loadNoticeTriggerGroup(),
      this.loadCitationNotices()
    ]);
  }

  private readonly subs = new Subscription();
  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private async loadTemplates(): Promise<void> {
    this.templatesLoaded = false;
    try {
      const all = await this.enforcementService.getAllDocumentTemplates();
      this.templates = (all || [])
        .filter((t: any) => (t?.TargetType || '').toString().toLowerCase() === 'citation')
        .map((t: any) => ({ Id: t.Id, Name: t.Name, TargetType: t.TargetType }))
        .sort((a: DocumentTemplateDto, b: DocumentTemplateDto) => (a.Name || '').localeCompare(b.Name || ''));
    } catch (error) {
      console.error('Failed to load document templates', error);
      this.toastr.error('Failed to load notice templates.');
      this.templates = [];
    } finally {
      this.templatesLoaded = true;
    }
  }

  public templateName(id: string | null | undefined): string | null | undefined {
    const t = this.templates.find(x => x.Id?.toLowerCase() === (id || '').toLowerCase());
    return t ? t.Name : undefined;
  }

  public noticeTemplateName(n: CitationNoticeListItem): string | null | undefined {
    return (n as any)?.DocumentTemplateName || this.templateName(n.DocumentTemplateId) || n.DocumentTemplateId;
  }

  public noticeGeneratedOn(n: CitationNoticeListItem): string {
    return n.GeneratedAtLocalFriendlyString || n.GeneratedAtUtc;
  }

  public noticeUrl(n: CitationNoticeListItem): string {
    return n.MediaId ? this.mediaService.GetMediaUrl(n.MediaId.toString()) : '';
  }

  public hasNoticePdf(n: CitationNoticeListItem): boolean {
    return !!n.MediaId;
  }

  private async loadCitation(id: string): Promise<void> {
    try {
      this.Loading();
      this.Model = await this.enforcementService.getCitation(+id);

      // Parking space information would need to be added to CitationResponse if needed for map display
    } finally {
      this.StopLoading();
    }
  }

  public async loadCitationNotices(): Promise<void> {
    if (!this.Model) return;
    this.noticesLoading = true;
    try {
      const resp = await this.enforcementService.getCitationNoticesForCitation(this.Model.Id, 100, "");
      const results = (resp && (resp.Results || resp.results)) ? (resp.Results || resp.results) : [];
      this.citationNotices = (results || []).slice().sort((a: any, b: any) => {
        const da = new Date(a.GeneratedAtUtc || a.GeneratedAtLocalFriendlyString || 0).getTime();
        const db = new Date(b.GeneratedAtUtc || b.GeneratedAtLocalFriendlyString || 0).getTime();
        return db - da;
      });
    } catch {
      this.citationNotices = [];
    } finally {
      this.noticesLoading = false;
    }
  }

  private async loadNoticeTriggerGroup(): Promise<void> {
    try {
      const g = await this.enforcementService.getSelectedNoticeTriggerSet();
      this.noticeTriggerGroup = g || null;
      const first = this.noticeTriggerGroup?.Triggers?.length ? this.noticeTriggerGroup.Triggers[0] : null;
      this.selectedNoticeTriggerId = first ? first.Id : null;
    } catch {
      this.noticeTriggerGroup = null;
      this.selectedNoticeTriggerId = null;
    }
  }

  nonCollectionTriggers() {
    return (this.noticeTriggerGroup?.Triggers || []).filter((x: any) => x.ActionType !== 1);
  }

  collectionTriggers() {
    return (this.noticeTriggerGroup?.Triggers || []).filter((x: any) => x.ActionType === 1);
  }

  public async generateNoticeLetter(): Promise<void> {
    if (!this.Model) return;
    if (!this.canGenerateNoticeLetters) return;
    if (!this.noticeTriggerGroup || !this.selectedNoticeTriggerId) return;

    const pdfWindow = window.open('about:blank', '_blank');
    if (!pdfWindow) {
      this.toastr.warning('Popup blocked — the PDF will open in this tab.');
    }

    this.noticeBusy = true;
    let shouldRefresh = false;
    try {
      const selected = (this.noticeTriggerGroup?.Triggers || []).find((x: any) => x?.Id === this.selectedNoticeTriggerId) as any;
      const actionType = selected?.ActionType ?? 0;

      if (actionType === 1) {
        await this.enforcementService.raiseCitationsToCollectionsByTrigger(
          [this.Model.Id],
          this.noticeTriggerGroup.Id,
          this.selectedNoticeTriggerId
        );
        shouldRefresh = true;
        this.toastr.success('Citation raised to collections.');
        if (pdfWindow) pdfWindow.close();
      } else {
        const resp = await this.enforcementService.generateCitationNoticesByTrigger(
          [this.Model.Id],
          this.noticeTriggerGroup.Id,
          this.selectedNoticeTriggerId,
          true
        );

        const notice = resp?.Notices?.length ? resp.Notices[0] : null;
        const mediaId = notice?.MediaId;
        shouldRefresh = true;

        // Optimistically add to list for immediate visibility (even if no PDF was produced).
        if (notice) {
          try {
            const optimistic: CitationNoticeListItem = {
              Id: notice.Id,
              CitationId: notice.CitationId,
              DocumentTemplateId: notice.DocumentTemplateId,
              DocumentTemplateName: notice.DocumentTemplateName,
              MediaId: notice.MediaId,
              GeneratedAtUtc: notice.GeneratedAtUtc,
              GeneratedAtLocalFriendlyString: notice.GeneratedAtLocalFriendlyString,
              Status: notice.Status,
              StatusName: notice.StatusName
            };
            this.citationNotices = [optimistic, ...(this.citationNotices || [])];
          } catch { /* ignore optimistic failures */ }
        }

        if (mediaId) {
          const url = this.mediaService.GetMediaUrl(mediaId.toString());
          if (pdfWindow) {
            pdfWindow.location.href = url;
            pdfWindow.focus();
          } else {
            window.location.href = url;
          }
        } else if (pdfWindow) {
          pdfWindow.close();
        }
      }
    } catch (error) {
      console.error('Failed to generate notice letter', error);
      this.toastr.error('Failed to generate notice letter.');
      if (pdfWindow) {
        pdfWindow.close();
      }
    } finally {
      this.noticeBusy = false;
      if (shouldRefresh) {
        try {
          await this.loadCitationNotices();
        } catch {
          // non-fatal
        }
      }
    }
  }

  private initializeCommunicationForm(): void {
    this.communicationForm = this.formBuilder.group({
      Direction: [CitationCommunicationDirection.Outbound, Validators.required],
      Channel: [CitationCommunicationChannel.Email, Validators.required],
      RecipientAddress: [''],
      Subject: [''],
      ExternalReference: [''],
      Content: ['', Validators.required]
    });
  }

  public async viewHistory(): Promise<void> {
    if (!this.Model) {
      return;
    }
    try {
      const history = await this.enforcementService.getCitationActionHistory(this.Model.Id);
      this.modalService.addModal(ActionHistoryModalComponent, {
        actions: history
      });
    } catch (error) {
      console.error('Error loading action history:', error);
    }
  }

  public async changeStatus(status: WorkflowTransitionDto): Promise<void> {
    if (!this.Model) {
      return;
    }
    const citationId = this.Model.Id;

    const modalRef = this.modalService.addModal(StatusChangeModalComponent, {
      statusId: status.Id,
      statusName: status.Name
    });

    this.subs.add(
      modalRef.subscribe(async (result) => {
        if (result) {
          try {
            this.Loading();
            this.Model = await this.enforcementService.changeCitationStatus(citationId, {
              StateKey: status.ToStateKey,
              Reason: result.reason
            });
          } finally {
            this.StopLoading();
          }
        }
      })
    );
  }

  public async submitCommunication(): Promise<void> {
    if (!this.Model || this.communicationForm.invalid) {
      this.communicationForm.markAllAsTouched();
      return;
    }

    const payload: AddCitationCommunicationRequest = {
      ...this.communicationForm.value,
      AttachmentMediaIds: []
    };

    try {
      this.Loading();
      const communication = await this.enforcementService.addCitationCommunication(this.Model.Id, payload);
      this.Model = {
        ...this.Model,
        Communications: [communication, ...(this.Model.Communications || [])]
      };
      this.communicationForm.reset({
        Direction: CitationCommunicationDirection.Outbound,
        Channel: CitationCommunicationChannel.Email,
        RecipientAddress: '',
        Subject: '',
        ExternalReference: '',
        Content: ''
      });
    } finally {
      this.StopLoading();
    }
  }

  public getDirectionLabel(direction: CitationCommunicationDirection): string {
    return CitationCommunicationDirection[direction];
  }

  public getChannelLabel(channel: CitationCommunicationChannel): string {
    return CitationCommunicationChannel[channel];
  }

  public getBaseFeeTotal(): number {
    if (!this.Model?.Infringements) {
      return 0;
    }
    // Base fee is the sum of ALL infringement base prices without any modifiers
    return this.Model.Infringements.reduce((sum, i) => sum + i.BasePrice, 0);
  }

  public isCurrentEscalation(index: number): boolean {
    if (!this.Model?.EscalationSchedule || this.Model.EscalationSchedule.length === 0) {
      return false;
    }
    const now = new Date();
    const schedule = this.Model.EscalationSchedule;

    // Find the current escalation level based on today's date
    for (let i = schedule.length - 1; i >= 0; i--) {
      const effectiveDate = new Date(schedule[i].EffectiveAfterDate);
      if (now >= effectiveDate) {
        return i === index;
      }
    }
    return index === 0; // Default to first item if no escalation is effective yet
  }

  public async applyFreeze(): Promise<void> {
    if (!this.Model || !this.freezeReason) {
      return;
    }
    if (this.freezeType === 'until' && !this.freezeUntilDate) {
      return;
    }

    this.freezeBusy = true;
    try {
      const request = {
        Reason: this.freezeReason,
        FrozenUntil: this.freezeType === 'until' ? this.freezeUntilDate : null
      };
      this.Model = await this.enforcementService.applyCitationFreeze(this.Model.Id, request);
      // Reset form
      this.freezeType = '';
      this.freezeUntilDate = '';
      this.freezeReason = '';
    } catch (error) {
      console.error('Error applying freeze:', error);
    } finally {
      this.freezeBusy = false;
    }
  }

  public async clearFreeze(): Promise<void> {
    if (!this.Model) {
      return;
    }

    this.freezeBusy = true;
    try {
      const request = this.clearFreezeReason ? { Reason: this.clearFreezeReason } : undefined;
      this.Model = await this.enforcementService.clearCitationFreeze(this.Model.Id, request);
      // Reset form
      this.clearFreezeReason = '';
    } catch (error) {
      console.error('Error clearing freeze:', error);
    } finally {
      this.freezeBusy = false;
    }
  }

  public openVoidModal(infringement: InfringementResponse): void {
    if (!this.Model || infringement.IsVoided) {
      return;
    }

    const modalRef = this.modalService.addModal(VoidInfringementModalComponent, {
      infringementId: infringement.Id,
      infringementTypeName: infringement.InfringementTypeName || 'Unknown'
    });

    this.subs.add(
      modalRef.subscribe(async (result) => {
        if (result && this.Model) {
          try {
            this.Loading();
            this.Model = await this.enforcementService.voidInfringement(
              this.Model.Id,
              infringement.Id,
              {
                VoidReasonId: result.voidReasonId,
                Comment: result.comment || null
              }
            );
          } catch (error) {
            console.error('Error voiding infringement:', error);
          } finally {
            this.StopLoading();
          }
        }
      })
    );
  }

  public addNote(): void {
    if (!this.Model) {
      return;
    }

    const modalRef = this.modalService.addModal(AddNoteModalComponent, {
      entityType: 'citation',
      entityId: this.Model.Id,
      entityReference: this.Model.Reference
    });

    this.subs.add(
      modalRef.subscribe(async (result) => {
        if (result && this.Model) {
          try {
            this.noteBusy = true;
            const note = await this.enforcementService.addCitationNote(this.Model.Id, {
              Content: result.content
            });
            // Add the new note to the beginning of the array (newest first)
            this.Model = {
              ...this.Model,
              Notes: [note, ...(this.Model.Notes || [])]
            };
          } catch (error) {
            console.error('Error adding note:', error);
          } finally {
            this.noteBusy = false;
          }
        }
      })
    );
  }

  public MakeManualPayment(): void {
    if (!this.Model) return;
    if (this.Model.IsWarning) return;

    this.subs.add(
      this.modalService.addModal(AddcitationmanualpaymentmodalComponent, { Citation: this.Model })
        .subscribe(async () => {
          if (this.Model) {
            try {
              await this.loadCitation(this.Model.Id.toString());
            } catch (error) {
              console.error('Failed to refresh citation after manual payment modal', error);
              this.toastr.error('Failed to refresh citation.');
            }
          }
        })
    );
  }

  public RecordRefund(payment: any): void {
    if (!this.Model) return;
    if (!payment) return;

    this.subs.add(
      this.modalService.addModal(CitationpaymentrefundComponent, { CitationId: this.Model.Id, Payment: payment })
        .subscribe(async (result) => {
          if (result && this.Model) {
            try {
              await this.loadCitation(this.Model.Id.toString());
            } catch (error) {
              console.error('Failed to refresh citation after refund modal', error);
              this.toastr.error('Failed to refresh citation.');
            }
          }
        })
    );
  }

  public showFreezeHighlightAfterAdjustment: boolean = false;
  @ViewChild('freezeSection') freezeSection!: ElementRef;

  public openAdjustmentModal() {
    const AdjustedFee = (this.Model?.TotalOutstanding ?? 0) + (this.Model?.TotalPaid ?? 0);

    this.subs.add(
      this.modalService.addModal(AddCitationAdjustmentComponent, { CitationId: this.Model?.Id, CitationPrice: AdjustedFee, })
        .subscribe(async (result) => {
          if (result && this.Model) {
            try {
              await this.loadCitation(this.Model.Id.toString());
              if (!this.Model.IsFrozen) {
                this.freezeSection?.nativeElement?.scrollIntoView({
                  behavior: 'smooth', // Smooth animation
                  block: 'start'      // Align the top of the element to the top of the viewport
                });
                this.showFreezeHighlightAfterAdjustment = true;
                setTimeout(() => {
                  this.showFreezeHighlightAfterAdjustment = false;
                }, 3000);
              }
            } catch (error) {
              console.error('Failed to refresh citation after adjustment modal', error);
              this.toastr.error('Failed to refresh citation.');
            }
          }
        })
    );
  }

  public cannotAddAdjustment(): boolean {
    return (this.Model?.TotalOutstanding ?? 0) + (this.Model?.TotalPaid ?? 0) <= 0
      || (this.Model?.Infringements != null && this.Model.Infringements.length > 0 && this.Model.Infringements.every(i => i.IsVoided));
  }

  public getTotalDiscount(): number {
    return this.Model?.Adjustments?.reduce((sum, a) => sum + a.Amount, 0) ?? 0;
  }

}
